import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

const LeipzigAbout = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Districts organized by region
  const versorgungsgebiete = {
    west: {
      title: 'Stadtbezirk West',
      areas: ['Plagwitz', 'Kleinzschocher', 'Großzschocher', 'Grünau', 'Grünau-Lausen', 'Schönau', 'Lindenau', 'Leutzsch', 'Schleußig']
    },
    suedwest: {
      title: 'Stadtbezirk Südwest',
      areas: ['Knauthain-Knautkleeberg', 'Knautnaundorf', 'Hartmannsdorf', 'Rehbach', 'Markkleeberg']
    },
    nordwest: {
      title: 'Stadtbezirk Nordwest',
      areas: ['Böhlitz-Ehrenberg', 'Burghausen-Rückmarsdorf', 'Wahren', 'Möckern']
    },
    nord: {
      title: 'Stadtbezirk Nord',
      areas: ['Gohlis', 'Eutritzsch', 'Wiederitzsch']
    },
    ost: {
      title: 'Stadtbezirk Ost',
      areas: ['Volkmarsdorf', 'Reudnitz', 'Thonberg', 'Stötteritz', 'Mölkau', 'Holzhausen']
    },
    suedost: {
      title: 'Stadtbezirk Südost',
      areas: ['Probstheida', 'Meusdorf']
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-turquoise-50 to-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative py-20 px-4 overflow-hidden" data-testid="about-hero">
        <div className="absolute inset-0 bg-gradient-to-r from-turquoise-400 to-turquoise-300 opacity-10"></div>
        <div className="container mx-auto text-center relative z-10">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            Über uns
          </h1>
          <p className="text-xl text-gray-600">Lernen Sie unser Team und unsere Werte kennen</p>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-gradient-to-br from-turquoise-50 to-turquoise-100 rounded-3xl shadow-xl p-8 md:p-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">Unsere Philosophie</h2>
            <p className="text-xl text-gray-700 leading-relaxed mb-6">
              <strong>Wir begannen mit einer einfachen Idee:</strong> Einen Pflegedienst zu gründen, welcher menschlich und ethisch aufgestellt ist und diese Werte auch in der Zukunft stets verfolgt.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              Aus der Sicht der <strong className="text-turquoise-400">Großstadtherzen</strong> hat jedes Individuum ein Anrecht auf eine würdevolle und respektierende Pflege und Betreuung, ganz unabhängig von finanziellen Mitteln.
            </p>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="container mx-auto max-w-5xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_bdf34f10-1a0a-4946-884b-914a0658d72a/artifacts/2xrnpwh3_picture-1600.jpg" 
                alt="Unser Team" 
                className="rounded-3xl shadow-2xl"
                data-testid="team-image"
              />
            </div>
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Unser Team</h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Unser Team besteht aus erfahrenen Fachkräften, welche Ihnen und Ihren Angehörigen stets zur Seite stehen.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Um eine kontinuierliche professionelle Pflege sowie innovativen Behandlungsmethoden zu gewährleisten, bilden wir uns regelmäßig fort.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Unser Leitbild</h2>
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-turquoise-50 to-turquoise-100 rounded-2xl p-6 shadow-lg border-l-4 border-turquoise-400">
              <div className="flex items-start space-x-4">
                <div className="bg-turquoise-400 text-white p-3 rounded-full flex-shrink-0">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <p className="text-lg text-gray-700">
                  Wir sehen den Klienten nicht nur als Kranken, hilfe- und pflegebedürftigen Menschen sondern als <strong>„Familienmitglied"</strong> des Systems
                </p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-turquoise-50 to-turquoise-100 rounded-2xl p-6 shadow-lg border-l-4 border-turquoise-400">
              <div className="flex items-start space-x-4">
                <div className="bg-turquoise-400 text-white p-3 rounded-full flex-shrink-0">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <p className="text-lg text-gray-700">
                  Wir <strong>halten</strong> unsere Zusagen und Versprechen
                </p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-2xl p-6 shadow-lg border-l-4 border-purple-600">
              <div className="flex items-start space-x-4">
                <div className="bg-purple-600 text-white p-3 rounded-full flex-shrink-0">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <p className="text-lg text-gray-700">
                  Wir <strong>achten</strong> unsere Familienmitglieder, unabhängig von deren Nationalität, Konfession, Herkunft, Hautfarbe, Geschlecht, sozialer Schicht, Krankheit oder Behinderung
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-3xl font-bold text-gray-900 mb-4 text-center">Versorgungsgebiete</h2>
          <p className="text-lg text-gray-600 mb-12 text-center max-w-3xl mx-auto">
            Leider können wir nicht komplett Leipzig versorgen. Aus diesem Grund mussten wir uns auf einzelne Gebiete konzentrieren.
          </p>

          {/* Districts Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Object.values(versorgungsgebiete).map((bezirk, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow"
              >
                <h3 className="text-xl font-bold text-turquoise-600 mb-4">{bezirk.title}</h3>
                <ul className="space-y-2">
                  {bezirk.areas.map((area, areaIndex) => (
                    <li key={areaIndex} className="flex items-center gap-2 text-gray-700">
                      <span className="w-2 h-2 bg-turquoise-400 rounded-full"></span>
                      {area}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MDK Certifications */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">MDK Prüfungen - Qualitätsnachweis</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-turquoise-50 to-turquoise-100 rounded-2xl shadow-xl p-6 text-center">
              <div className="bg-turquoise-400 text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                ✓
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">2025</h3>
              <p className="text-gray-600 mb-2">MDK Prüfung bestanden</p>
              <p className="text-2xl font-bold text-turquoise-400">Note 1,0</p>
            </div>
            <div className="bg-gradient-to-br from-turquoise-50 to-turquoise-100 rounded-2xl shadow-xl p-6 text-center">
              <div className="bg-turquoise-400 text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                ✓
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">2023</h3>
              <p className="text-gray-600 mb-2">MDK Prüfung bestanden</p>
              <p className="text-2xl font-bold text-turquoise-400">Note 1,0</p>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl shadow-xl p-6 text-center">
              <div className="bg-purple-600 text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                ✓
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">2021</h3>
              <p className="text-gray-600 mb-2">MDK Prüfung bestanden</p>
              <p className="text-2xl font-bold text-purple-600">Note 1,0</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-gradient-to-br from-turquoise-400 to-turquoise-400 text-white">
        <div className="container mx-auto text-center max-w-4xl">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Überzeugt?
          </h2>
          <p className="text-xl mb-8">
            Fordern Sie jetzt ein unverbindliches Angebot an
          </p>
          <Link 
            to="/angebot"
            className="inline-block bg-white text-turquoise-400 px-8 py-4 rounded-full text-lg font-semibold hover:bg-turquoise-50 transition-all shadow-lg"
            data-testid="cta-angebot"
          >
            Jetzt Angebot anfordern →
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LeipzigAbout;
